var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { DataTable } from 'mantine-datatable';
import { useDebouncedValue } from '@mantine/hooks';
import { Box, TextInput, Space, Group } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import '@mantine/core/styles.layer.css';
import 'mantine-datatable/styles.layer.css';
import './layout.css';
import Filter from './Filter';
import ColumnVisibility from './ColumnVisibility';
export default function Table(_a) {
    var _b, _c, _d;
    var _e = _a.rows, rows = _e === void 0 ? [] : _e, _f = _a.columns, columns = _f === void 0 ? [] : _f, _g = _a.height, height = _g === void 0 ? 'calc(100vh - 200px)' : _g, _h = _a.width, width = _h === void 0 ? '100%' : _h, options = _a.options, _j = _a.lang, lang = _j === void 0 ? 'fr' : _j, _k = _a.fetching, fetching = _k === void 0 ? false : _k, _l = _a.onSelect, onSelect = _l === void 0 ? undefined : _l, rowExpansion = _a.rowExpansion, _m = _a.rightCustomActions, rightCustomActions = _m === void 0 ? [] : _m, _o = _a.leftCustomActions, leftCustomActions = _o === void 0 ? [] : _o, _p = _a.withPagination, withPagination = _p === void 0 ? true : _p, _q = _a.pageSize, pageSize = _q === void 0 ? 10000 : _q, restProps = __rest(_a, ["rows", "columns", "height", "width", "options", "lang", "fetching", "onSelect", "rowExpansion", "rightCustomActions", "leftCustomActions", "withPagination", "pageSize"]);
    var language = lang === 'fr' || lang === 'en' ? lang : 'fr';
    var _r = useState((options === null || options === void 0 ? void 0 : options.sort) ? options.sort : { columnAccessor: '', direction: 'asc' }), sortStatus = _r[0], setSortStatus = _r[1];
    var _s = useState((rows === null || rows === void 0 ? void 0 : rows.length) || 0), totalRecords = _s[0], setTotalRecords = _s[1];
    var _t = useState(1), page = _t[0], setPage = _t[1];
    var _u = useState(rows.slice(0, pageSize)), items = _u[0], setItems = _u[1];
    var _v = useState([]), selectedRecords = _v[0], setSelectedRecords = _v[1];
    var _w = useState(''), query = _w[0], setQuery = _w[1];
    var debouncedQuery = useDebouncedValue(query, 200)[0];
    var translations = useMemo(function () { return ({
        fr: {
            noData: 'Pas de données',
            loading: 'Chargement...',
            searchBy: 'Rechercher par',
            to: 'à',
            total: 'total',
        },
        en: {
            noData: 'No data',
            loading: 'Loading...',
            searchBy: 'Search by',
            to: 'to',
            total: 'total',
        },
    }); }, []);
    var selectionProps = useMemo(function () { return (onSelect ? { selectedRecords: selectedRecords, onSelectedRecordsChange: onSelectedRecordsChange } : {}); }, [onSelect, selectedRecords]);
    var effectiveColumns = ((_b = options === null || options === void 0 ? void 0 : options.hiddenColumns) === null || _b === void 0 ? void 0 : _b.length)
        ? columns === null || columns === void 0 ? void 0 : columns.filter(function (item) { var _a; return !((_a = options === null || options === void 0 ? void 0 : options.hiddenColumns) === null || _a === void 0 ? void 0 : _a.includes(item.accessor)); })
        : columns;
    var pageProps = useMemo(function () {
        if (withPagination) {
            return {
                page: page,
                onPageChange: function (p) { return setPage(p); },
                totalRecords: totalRecords,
                recordsPerPage: pageSize,
                paginationText: function (_a) {
                    var from = _a.from, to = _a.to, totalRecords = _a.totalRecords;
                    return "".concat(from, " ").concat(translations[language].to, " ").concat(to, " (").concat(translations[language].total, " : ").concat(totalRecords, ")");
                },
                loadingText: translations[language].loading,
            };
        }
        return {};
    }, [page, setPage, totalRecords]);
    useEffect(function () {
        applySearchFilter();
    }, [page, sortStatus, rows, debouncedQuery, options]);
    function applySearchFilter() {
        var from = (page - 1) * pageSize;
        var to = from + pageSize;
        var _items = JSON.parse(JSON.stringify(rows));
        _items = sort(_items);
        _items = search(_items);
        _items = filter(_items);
        if (to !== pageSize && _items.length <= pageSize) {
            setPage(Math.ceil(_items.length / pageSize));
        }
        setTotalRecords(_items.length);
        setItems(_items.slice(from, to));
    }
    function sort(_items) {
        if (!(sortStatus === null || sortStatus === void 0 ? void 0 : sortStatus.columnAccessor))
            return _items;
        var data = sortBy(_items, sortStatus.columnAccessor);
        return (sortStatus === null || sortStatus === void 0 ? void 0 : sortStatus.direction) === 'desc' ? data.reverse() : data;
    }
    function search(_items) {
        return _items.filter(function (row) {
            var _a, _b;
            var accessors = (_b = (_a = options === null || options === void 0 ? void 0 : options.search) === null || _a === void 0 ? void 0 : _a.accessor) === null || _b === void 0 ? void 0 : _b.split('.');
            var value = row;
            accessors === null || accessors === void 0 ? void 0 : accessors.forEach(function (item) {
                value = value === null || value === void 0 ? void 0 : value[item];
            });
            if (value == null)
                return false;
            return !(debouncedQuery !== '' &&
                !value
                    .toString()
                    .toLowerCase()
                    .includes(debouncedQuery.trim().toLowerCase()));
        });
    }
    function filter(_items) {
        var _a;
        if ((_a = options === null || options === void 0 ? void 0 : options.filter) === null || _a === void 0 ? void 0 : _a.length) {
            var newItems_1 = __spreadArray([], _items, true);
            options.filter.forEach(function (item) {
                var _a;
                if ((_a = item.selectedChoices) === null || _a === void 0 ? void 0 : _a.length) {
                    var searchValues_1 = item.selectedChoices.map(function (elt) {
                        return elt.value.toLowerCase();
                    });
                    newItems_1 = newItems_1.filter(function (row) {
                        var accessors = item.accessor.split('.');
                        var value = row;
                        accessors.forEach(function (item) {
                            value = value === null || value === void 0 ? void 0 : value[item];
                        });
                        if (value == null)
                            return false;
                        return (searchValues_1.findIndex(function (v) { return v === value.toString().toLowerCase(); }) !== -1);
                    });
                }
            });
            return newItems_1;
        }
        return _items;
    }
    function onSelectedRecordsChange(_records) {
        setSelectedRecords(_records);
        if (onSelect) {
            onSelect(_records);
        }
    }
    function updateFilterChoice(item, selectedChoices) {
        var index = item.selectedChoices
            .map(function (item) { return item.value; })
            .findIndex(function (value) { return value === selectedChoices.value; });
        if (index === -1) {
            item.selectedChoices.push(selectedChoices);
        }
        else {
            item.selectedChoices.splice(index, 1);
        }
        if (options && options.updateFilter && options.filter) {
            options.updateFilter(options.filter);
        }
    }
    return (React.createElement(Box, { w: width, h: height },
        (options ||
            (rightCustomActions === null || rightCustomActions === void 0 ? void 0 : rightCustomActions.length) > 0 ||
            (leftCustomActions === null || leftCustomActions === void 0 ? void 0 : leftCustomActions.length) > 0) && (React.createElement(React.Fragment, null,
            React.createElement(Group, { justify: 'space-between' },
                React.createElement(Group, null,
                    (options === null || options === void 0 ? void 0 : options.hiddenColumns) && (React.createElement(ColumnVisibility, { columns: columns === null || columns === void 0 ? void 0 : columns.map(function (item) { return ({
                            accessor: item.accessor,
                            title: item.title,
                        }); }), hiddenColumns: options === null || options === void 0 ? void 0 : options.hiddenColumns, updateHiddenColumns: options === null || options === void 0 ? void 0 : options.updateHiddenColumns })),
                    (leftCustomActions === null || leftCustomActions === void 0 ? void 0 : leftCustomActions.length) > 0 && (React.createElement(Group, null, leftCustomActions === null || leftCustomActions === void 0 ? void 0 : leftCustomActions.map(function (item) { return item; }))),
                    (options === null || options === void 0 ? void 0 : options.search) && (React.createElement(TextInput, { placeholder: "".concat(translations[language].searchBy, " ").concat(options.search.label), leftSection: React.createElement(IconSearch, { size: 16 }), value: query, onChange: function (e) { return setQuery(e.currentTarget.value); } })),
                    Boolean((_c = options === null || options === void 0 ? void 0 : options.filter) === null || _c === void 0 ? void 0 : _c.length) &&
                        ((_d = options === null || options === void 0 ? void 0 : options.filter) === null || _d === void 0 ? void 0 : _d.map(function (item) { return (React.createElement(Filter, { key: item.accessor, option: item, onChoiceClick: function (choice) { return updateFilterChoice(item, choice); } })); }))),
                (rightCustomActions === null || rightCustomActions === void 0 ? void 0 : rightCustomActions.length) > 0 && (React.createElement(Group, null, rightCustomActions === null || rightCustomActions === void 0 ? void 0 : rightCustomActions.map(function (item) { return item; })))),
            React.createElement(Space, { h: "xs" }))),
        React.createElement(DataTable, __assign({ height: "calc(100% - 52px)", fetching: fetching, withTableBorder: true, borderRadius: "sm", striped: true, highlightOnHover: true, columns: effectiveColumns, noRecordsText: translations[language].noData, rowExpansion: rowExpansion, sortStatus: sortStatus, onSortStatusChange: setSortStatus, records: withPagination ? items : rows }, pageProps, selectionProps, restProps))));
}
